/**
 * UTM Tracking Hook
 *
 * Captures and persists UTM parameters on landing for attribution tracking.
 * Integrates with Usermaven for conversion attribution.
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  extractUTMParams,
  storeUTMParams,
  getStoredUTMParams,
  captureArrival,
  type UTMParams,
  type ClickIds,
} from '@/lib/marketing/utm-builder';
import { trackPageViewed, trackContentsViewed } from '@/lib/analytics/openai-ads';

// ============================================================================
// Types
// ============================================================================

interface UTMContextValue {
  /** Current UTM params (from URL or storage) */
  utmParams: Partial<UTMParams>;
  /** Google Ads click IDs (gclid/gbraid/wbraid) from URL or 90-day cookie */
  clickIds: ClickIds;
  /** Whether UTM params have been captured this session */
  hasCaptured: boolean;
  /** Force refresh UTM params from URL */
  refreshParams: () => void;
  /** Get UTM params formatted for conversion tracking */
  getUTMForConversion: () => Record<string, string>;
  /** Get all attribution data (UTM + click IDs) for signup insertion */
  getAttributionData: () => Record<string, string>;
}

interface UseUTMTrackingOptions {
  /** Whether to track arrival/landing page view (default: true) */
  trackArrival?: boolean;
  /** Debug mode - logs UTM activity to console */
  debug?: boolean;
}

// ============================================================================
// Context
// ============================================================================

const UTMContext = createContext<UTMContextValue | null>(null);

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to capture and access UTM parameters
 *
 * @example
 * ```tsx
 * function LandingPage() {
 *   const { utmParams, getUTMForConversion } = useUTMTracking();
 *
 *   const handleSignup = async (email: string) => {
 *     // Include UTM params in signup for attribution
 *     await signup({ email, ...getUTMForConversion() });
 *   };
 *
 *   return <SignupForm onSubmit={handleSignup} />;
 * }
 * ```
 */
export function useUTMTracking(options: UseUTMTrackingOptions = {}) {
  const { trackArrival = true, debug = false } = options;
  const location = useLocation();
  const [utmParams, setUtmParams] = useState<Partial<UTMParams>>({});
  const [clickIds, setClickIds] = useState<ClickIds>({});
  const [hasCaptured, setHasCaptured] = useState(false);

  // Capture UTM params and click IDs on mount and URL changes
  useEffect(() => {
    // ONE call, not three. The UTM params, the click ids, and the landing path
    // ship together in a single `ad_click_attributions` row and are read as one
    // story, so they are captured as one unit — `captureArrival` detects whether
    // this arrival begins a new campaign episode BEFORE it writes any of them,
    // which is impossible to do from three independent calls because each one
    // overwrites the stored side of that comparison.
    //
    // This hook is mounted app-wide in App.tsx, so this effect is the earliest
    // point at which the arrival URL is observable — the only point at which it
    // is still true. `ad_click_attributions.landing_page` used to be read at POST
    // time inside AuthForm/PricingPage and therefore always recorded /auth or
    // /pricing (all 162 prod rows, verified 2026-09-07).
    //
    // Landing page is first-write-wins WITHIN an episode and re-armed ACROSS
    // episodes, so ordinary navigation leaves it alone while a fresh ad click in
    // an already-open tab updates it alongside the UTMs it arrived with.
    const { utmParams: params, clickIds: ids, landingPage } = captureArrival(
      location.pathname
    );

    if (Object.keys(ids).length > 0) {
      setClickIds(ids);
      if (debug) {
        console.log('[UTM] Captured click IDs:', ids);
      }
    }

    if (Object.keys(params).length > 0) {
      setUtmParams(params);
      setHasCaptured(true);

      if (debug) {
        console.log('[UTM] Captured params:', params);
      }

      // Track arrival with Usermaven if configured
      if (trackArrival && typeof window !== 'undefined' && (window as any).usermaven) {
        (window as any).usermaven('track', 'utm_arrival', {
          ...params,
          // The first-touch value, not the live pathname — same field name, same
          // meaning as the `landing_page` column, so the two agree by
          // construction rather than by coincidence.
          landing_page: landingPage,
        });
      }
    } else {
      // Check for stored params
      const stored = getStoredUTMParams();
      if (Object.keys(stored).length > 0) {
        setUtmParams(stored);
        if (debug) {
          console.log('[UTM] Using stored params:', stored);
        }
      }
    }
  }, [location.search, trackArrival, debug]);

  // OpenAI Ads Measurement Pixel — SPA page views.
  //
  // The pixel doc documents no automatic `page_viewed` and no history.pushState
  // handling (not re-verified against the SDK source, so manual firing is the
  // safe assumption). This effect keys on `location.pathname` rather than
  // sharing the capture effect above, which keys on `location.search` and so
  // would miss a route change that keeps the query string.
  //
  // Both helpers no-op when the pixel is absent (flag off, non-prod hostname,
  // ad blocker) or when GPC is set. Firing unconditionally is intentional.
  useEffect(() => {
    trackPageViewed();

    // Sales pages get an extra `contents_viewed`. `/bar/practice` is the public
    // NextGen Practice page (it replaced /nextgen-prep, which 301s here as of
    // 2026-09-11); `/nextgen` redirects into the auth-gated practice area and
    // is never an ad landing surface.
    if (location.pathname === '/' || location.pathname === '/bar/practice') {
      trackContentsViewed();
    }
  }, [location.pathname]);

  // Refresh params from URL
  const refreshParams = useCallback(() => {
    const params = extractUTMParams();
    if (Object.keys(params).length > 0) {
      storeUTMParams(params);
      setUtmParams(params);
      setHasCaptured(true);

      if (debug) {
        console.log('[UTM] Refreshed params:', params);
      }
    }
  }, [debug]);

  // Get UTM params formatted for conversion tracking
  const getUTMForConversion = useCallback((): Record<string, string> => {
    const result: Record<string, string> = {};
    Object.entries(utmParams).forEach(([key, value]) => {
      if (value) {
        result[key] = value;
      }
    });
    return result;
  }, [utmParams]);

  // Get all attribution data (UTM + click IDs) for ad_click_attributions insertion
  const getAttributionData = useCallback((): Record<string, string> => {
    const result: Record<string, string> = {};
    Object.entries(utmParams).forEach(([key, value]) => {
      if (value) result[key] = value;
    });
    Object.entries(clickIds).forEach(([key, value]) => {
      if (value) result[key] = value;
    });
    return result;
  }, [utmParams, clickIds]);

  return {
    utmParams,
    clickIds,
    hasCaptured,
    refreshParams,
    getUTMForConversion,
    getAttributionData,
  };
}

// ============================================================================
// Provider
// ============================================================================

interface UTMProviderProps {
  children: React.ReactNode;
  debug?: boolean;
}

/**
 * Provider component for UTM tracking context
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <UTMProvider debug={process.env.NODE_ENV === 'development'}>
 *       <Router>
 *         <Routes />
 *       </Router>
 *     </UTMProvider>
 *   );
 * }
 * ```
 */
export function UTMProvider({ children, debug = false }: UTMProviderProps) {
  const tracking = useUTMTracking({ debug });

  return (
    <UTMContext value={tracking}>
      {children}
    </UTMContext>
  );
}

/**
 * Hook to access UTM context (must be used within UTMProvider)
 */
export function useUTMContext(): UTMContextValue {
  const context = useContext(UTMContext);
  if (!context) {
    throw new Error('useUTMContext must be used within a UTMProvider');
  }
  return context;
}

export default useUTMTracking;
